import { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import type { ReferenceZone } from './MapboxPolygonEditor';
import { env } from '@/env';

const TOKEN = env.mapboxToken;

const DEFAULT_CENTER: [number, number] = [13.405, 52.52];

/**
 * A read-only map that renders every drawn delivery-ZONE polygon at once, each
 * in its own color with its label - a bird's-eye overview above the zone list.
 * No draw tools, no distance circles: these are the owner's map zones only. The
 * list below is where a zone's fee/minimum is inspected and edited; this is just
 * the "what's covered" picture.
 */
export function MapboxZonesPreview({
  zones,
  center,
  height = 220,
}: {
  // Already-colored polygon zones (rings with ≥3 points). Assign colors with the
  // shared `zoneColor` so a zone matches its color in the editor and list.
  zones: ReferenceZone[];
  center?: { latitude: number; longitude: number } | null;
  height?: number;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  // Latest zones, so the load handler (which runs once) reads current data.
  const zonesRef = useRef(zones);
  zonesRef.current = zones;

  useEffect(() => {
    if (!TOKEN || !containerRef.current || mapRef.current) return;
    mapboxgl.accessToken = TOKEN;
    const drawable = zonesRef.current.filter((z) => z.ring && z.ring.length >= 3);
    const start: [number, number] = center
      ? [center.longitude, center.latitude]
      : drawable.length
        ? (drawable[0].ring[0] as [number, number])
        : DEFAULT_CENTER;

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: start,
      zoom: 12,
    });

    map.on('load', () => {
      const bounds = new mapboxgl.LngLatBounds();
      let hasBounds = false;
      const features = drawable.map((z) => {
        z.ring.forEach(([lng, lat]) => {
          bounds.extend([lng, lat]);
          hasBounds = true;
        });
        return {
          type: 'Feature' as const,
          properties: { label: z.label, color: z.color },
          geometry: { type: 'Polygon' as const, coordinates: [z.ring] },
        };
      });

      // Restaurant location pin - the origin the zones are drawn around; kept in
      // view so the overview always shows where the shop sits.
      if (center) {
        new mapboxgl.Marker({ color: '#ef4444' })
          .setLngLat([center.longitude, center.latitude])
          .addTo(map);
        bounds.extend([center.longitude, center.latitude]);
        hasBounds = true;
      }

      if (features.length) {
        map.addSource('zones', {
          type: 'geojson',
          data: { type: 'FeatureCollection', features } as GeoJSON.FeatureCollection,
        });
        map.addLayer({
          id: 'zones-fill',
          type: 'fill',
          source: 'zones',
          paint: { 'fill-color': ['get', 'color'], 'fill-opacity': 0.22 },
        });
        map.addLayer({
          id: 'zones-line',
          type: 'line',
          source: 'zones',
          paint: { 'line-color': ['get', 'color'], 'line-width': 2 },
        });
        map.addLayer({
          id: 'zones-label',
          type: 'symbol',
          source: 'zones',
          layout: { 'text-field': ['get', 'label'], 'text-size': 12 },
          paint: { 'text-color': '#1f2937', 'text-halo-color': '#ffffff', 'text-halo-width': 1.5 },
        });
      }

      if (hasBounds) map.fitBounds(bounds, { padding: 32, duration: 0, maxZoom: 15 });
    });

    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
    // Init once; the parent re-mounts (via key) when the zone set changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!TOKEN) return null;

  return <div ref={containerRef} style={{ width: '100%', height }} />;
}
